<script setup>
/**
 * Top50Modal - カテゴリのTOP50商品をおしゃれに表示するモーダルコンポーネント
 * 
 * 使用例:
 * <Top50Modal 
 *   :show="showTop50Modal" 
 *   :items="apiItems" 
 *   :category="selectedCategory"
 *   :category-name="categoryNames[selectedCategory]"
 *   @close="handleCloseModal" 
 * />
 */
import { ref, computed, watch, nextTick } from 'vue';
import BannerAd from './BannerAd.vue';
import { useDeviceDetection, truncateText, useModalScroll, calculateAdSize } from '@/services/uiUtils';

// eslint-disable-next-line no-undef
const props = defineProps({
  /**
   * モーダル表示フラグ
   */
  show: {
    type: Boolean,
    default: false
  },
  
  /**
   * 表示アイテムリスト
   */
  items: {
    type: Array,
    default: () => []
  },
  
  /**
   * カテゴリID
   */
  category: {
    type: String,
    default: 'toys'
  },
  
  /**
   * カテゴリ表示名
   */
  categoryName: {
    type: String,
    default: 'カテゴリ'
  },
  
  /**
   * モバイル判定の閾値 (px)
   */
  mobileBreakpoint: {
    type: Number,
    default: 740
  }
});

// eslint-disable-next-line no-undef
const emit = defineEmits(['close', 'item-click']);

// デバイス検出とモーダルスクロール制御のユーティリティを使用
const { isMobile } = useDeviceDetection(props.mobileBreakpoint);
const { disableScroll, enableScroll } = useModalScroll();

// モーダル表示状態の監視
watch(() => props.show, (newVal) => {
  if (newVal) {
    disableScroll(); // スクロール防止
  } else {
    enableScroll(); // スクロール復活
  }
});

// 商品カードサイズの計算
const cardSize = computed(() => calculateAdSize(isMobile.value, 100, 140));
const gridColumns = computed(() => isMobile.value ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)');

// 商品クリックハンドラ
const handleItemClick = (data) => {
  emit('item-click', data);
};

// モーダルを閉じるハンドラ
const closeModal = () => {
  emit('close');
};

// ESCキーでモーダルを閉じる
const handleKeydown = (event) => {
  if (event.key === 'Escape' && props.show) {
    closeModal();
  }
};

// モーダルの外側をクリックしたら閉じる
const handleOutsideClick = (event) => {
  if (event.target.classList.contains('modal-overlay')) {
    closeModal();
  }
};

// ESCキーのイベントリスナー設定
watch(() => props.show, (newVal) => {
  if (newVal) {
    window.addEventListener('keydown', handleKeydown);
  } else {
    window.removeEventListener('keydown', handleKeydown);
  }
}, { immediate: true });

// アニメーション用タイマー
const itemsVisible = ref(false);
watch(() => props.show, async (newVal) => {
  if (newVal) {
    // モーダルが表示されてから少し待ってからアイテムをアニメーション表示
    await nextTick();
    setTimeout(() => {
      itemsVisible.value = true;
    }, 300);
  } else {
    itemsVisible.value = false;
  }
});

// 商品名を制限する関数
const truncateProductName = (title) => truncateText(title, 24);
</script>

<template>
  <transition name="modal-fade">
    <div v-if="show" class="modal-overlay" @click="handleOutsideClick">
      <div class="modal-container" :class="{ 'is-mobile': isMobile }">
        <!-- モーダルヘッダー -->
        <div class="modal-header">
          <h3>{{ categoryName }}のTOP20</h3>
          <button class="close-button" @click="closeModal">×</button>
        </div>
        
        <!-- モーダルコンテンツ -->
        <div class="modal-content">
          <transition-group name="item-fade" tag="div" class="items-grid" :style="{ gridTemplateColumns: gridColumns }">
            <div v-for="(item, index) in items.slice(0, 50)" :key="index" class="item-wrapper" :class="{ 'visible': itemsVisible }">
              <div class="rank-badge">{{ index + 1 }}</div>
              <BannerAd 
                :productName="truncateProductName(item.title || '商品名')"
                :discount="item.discountPercent ? `${item.discountPercent}%OFF` : ''"
                :backgroundImage="item.image"
                :backgroundColor="!item.image ? `linear-gradient(135deg, hsl(${index * 7}, 70%, 60%), hsl(${index * 7 + 30}, 70%, 40%))` : ''"
                :size="cardSize"
                :borderRadius="20"
                :url="item.url"
                :openInNewTab="true"
                @click="handleItemClick"
              />
              <div class="item-price">
                <span v-if="item.price">{{ item.price }}</span>
                <span v-else>価格情報なし</span>
              </div>
            </div>
          </transition-group>
        </div>
      </div>
    </div>
  </transition>
</template>

<style scoped>
/* モーダルオーバーレイ */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  box-sizing: border-box;
}

/* モーダルコンテナ */
.modal-container {
  width: 95%;
  max-width: 900px;
  max-height: 90vh;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(10px) saturate(180%);
  -webkit-backdrop-filter: blur(10px) saturate(180%);
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(255, 255, 255, 0.15) inset;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* モーダルヘッダー */
.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(5px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.modal-header h3 {
  margin: 0;
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.close-button {
  background: none;
  border: none;
  font-size: 28px;
  color: #555;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

/* モーダルコンテンツ */
.modal-content {
  padding: 15px;
  overflow-y: auto;
  overflow-x: hidden;
  flex: 1;
}

/* 商品グリッド */
.items-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
}

/* 商品ラッパー */
.item-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  transition: transform 0.3s ease, opacity 0.5s ease;
  opacity: 0;
  transform: translateY(20px);
}

.item-wrapper.visible {
  opacity: 1;
  transform: translateY(0);
}

/* アイテムが順番に表示されるアニメーション */
.item-wrapper:nth-child(1) { transition-delay: 0.05s; }
.item-wrapper:nth-child(2) { transition-delay: 0.1s; }
.item-wrapper:nth-child(3) { transition-delay: 0.15s; }
.item-wrapper:nth-child(4) { transition-delay: 0.2s; }
.item-wrapper:nth-child(5) { transition-delay: 0.25s; }
.item-wrapper:nth-child(6) { transition-delay: 0.3s; }
.item-wrapper:nth-child(7) { transition-delay: 0.35s; }
.item-wrapper:nth-child(8) { transition-delay: 0.4s; }
.item-wrapper:nth-child(9) { transition-delay: 0.45s; }
.item-wrapper:nth-child(10) { transition-delay: 0.5s; }

/* 順位バッジ */
.rank-badge {
  position: absolute;
  top: -10px;
  left: -10px;
  width: 30px;
  height: 30px;
  background-color: #ffcc00;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: #000;
  z-index: 10;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
  font-size: 14px;
}

/* 1位〜3位の特別スタイル */
.item-wrapper:nth-child(1) .rank-badge {
  background: linear-gradient(135deg, #ffd700, #e6b800);
  width: 36px;
  height: 36px;
  font-size: 18px;
}

.item-wrapper:nth-child(2) .rank-badge {
  background: linear-gradient(135deg, #C0C0C0, #A9A9A9);
  width: 34px;
  height: 34px;
  font-size: 16px;
}

.item-wrapper:nth-child(3) .rank-badge {
  background: linear-gradient(135deg, #CD7F32, #A46628);
  width: 32px;
  height: 32px;
  font-size: 15px;
}

/* 商品価格 */
.item-price {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

/* モーダルフェードアニメーション */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* モバイル対応 */
.is-mobile .modal-header h3 {
  font-size: 18px;
}

.is-mobile .modal-content {
  padding: 15px;
}

.is-mobile .items-grid {
  gap: 10px;
}

.is-mobile .item-price {
  font-size: 12px;
}

.is-mobile .rank-badge {
  width: 24px;
  height: 24px;
  font-size: 12px;
  top: 0px;
  left: 5px;
}

.is-mobile .item-wrapper:nth-child(1) .rank-badge {
  width: 30px;
  height: 30px;
  font-size: 16px;
}

.is-mobile .item-wrapper:nth-child(2) .rank-badge,
.is-mobile .item-wrapper:nth-child(3) .rank-badge {
  width: 28px;
  height: 28px;
  font-size: 14px;
}
</style>